import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/auth/context'
import { usePermissions } from '@/hooks/usePermissions'
import { Spinner } from '@/components/ui'
import { ROUTES, landingPathForUser } from '@/config/routes'
import { useImpersonation } from '@/stores/impersonation.store'
import type { Permission } from '@/config/constants'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredPermission?: Permission
  requiredPermissions?: Permission[]
  requireAll?: boolean
}

export function ProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions()
  const location = useLocation()
  const impersonation = useImpersonation()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />
  }

  if (
    user?.role === 'super_admin' &&
    !impersonation.active &&
    !location.pathname.startsWith('/admin')
  ) {
    return <Navigate to={landingPathForUser(user)} replace />
  }

  // Check single permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />
  }

  // Check multiple permissions
  if (requiredPermissions) {
    const hasAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions)

    if (!hasAccess) {
      return <Navigate to={ROUTES.DASHBOARD} replace />
    }
  }

  return <>{children}</>
}
